import { Chip } from '@progress/kendo-react-buttons';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { GridRowProps } from '@progress/kendo-react-grid';
import { cloneElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useGLSummaryColumns from 'src/assets/static/action_required_invoices/GLSummaryColumns';
import GridWrapper from 'src/components/GridWrapper';
import Button from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import useActionRequiredInvoices from 'src/hooks/useActionRequiredInvoices';
import useBoolean from 'src/hooks/useBoolean';
import { fToMMDDYY } from 'src/utils/formatTime';
import TransactionTable from '../view_checkbook/TransactionTable';

const defaultProps = {
  DataItemKey: 'rowId',
  sortable: false,
  resizable: true,
  showColumnMenu: false,
};

const GLSummaryGrid = () => {
  const {
    glSummaryDataTable,
    actionRequiredLoading,
    additionalGLCodeFilter,
    setAdditionalGLCodeFilter,
    selectedGLSummaryRowId,
    setSelectedGLSummaryRowId,

    fiscalYearStartDate,

    selectedGLSummary,
    setSelectedGLSummary,
    fiscalYearEastEndDate,
    usesBudgets,
  } = useActionRequiredInvoices();

  const { t } = useTranslation();
  const GLSummaryColumns = useGLSummaryColumns();

  const handleClearFilter = useCallback(() => {
    setAdditionalGLCodeFilter('');
    setSelectedGLSummaryRowId('');
  }, [setAdditionalGLCodeFilter, setSelectedGLSummaryRowId]);

  const rowRender = useCallback(
    (trElement: React.ReactElement<HTMLTableRowElement>, props: GridRowProps) => {
      const rowId = props?.dataItem?.rowId;
      const blue = { backgroundColor: '#3fa9db' };
      let trProps: any = {};
      if (rowId === selectedGLSummaryRowId) {
        trProps = { style: blue };
      }
      return cloneElement(trElement, { ...trProps });
    },
    [selectedGLSummaryRowId]
  );

  const {
    value: isOpenTransaction,
    setFalse: closeTransaction,
    setTrue: openTransaction,
  } = useBoolean(false);

  const handleCellClick = useCallback(
    (dataItem: any) => {
      setSelectedGLSummary(dataItem);
      openTransaction();
    },
    [openTransaction, setSelectedGLSummary]
  );
  const FYDescription = `FY${selectedGLSummary?.fiscalYear} (${fToMMDDYY(
    fiscalYearStartDate
  )} - ${fToMMDDYY(fiscalYearEastEndDate)}) `;

  return (
    <div style={{ height: '39%' }}>
      <p className="font-semibold text-sm mb-2">
        {usesBudgets
          ? ` Current Forecast and Budget Remaining Balances does not include invoice variances`
          : `Current Forecast Remaining Balance does not include invoice variances`}
      </p>
      <div className="glSummaryGridCover flex flex-col">
        {!!additionalGLCodeFilter && (
          <Chip
            size={'small'}
            className="mb-1 w-fit"
            rounded={'large'}
            removable
            text="Clear Selection"
            onClick={handleClearFilter}
          />
        )}
        <GridWrapper
          rowRender={rowRender}
          columns={GLSummaryColumns}
          dataSource={glSummaryDataTable}
          hideScrollBar
          total={0}
          className="glSummaryGrid"
          loading={actionRequiredLoading}
          cellClick={handleCellClick}
          {...defaultProps}
        />
      </div>

      <Dialog
        width={'80%'}
        id="modal"
        title={
          <div>
            <p className="font-bold mb-1">
              {t(`Checkbook Transactions - ${selectedGLSummary?.description}`)}
            </p>
            <p className="text-xs font-medium">
              <span>
                {t('GL Code')}: <span className="font-bold">{selectedGLSummary?.glCd}</span>
              </span>
              <span className="text-[#AAAAAA]"> | </span>
              <span>
                {t('Fiscal year')}: <span className="font-bold">{FYDescription}</span>
              </span>
              <span className="text-[#AAAAAA]"> | </span>

              <span>
                {t(`Accounting period :`)}{' '}
                <span className="font-bold">
                  ({fToMMDDYY(selectedGLSummary?.budgetPeriodStartDate || null)}-
                  {fToMMDDYY(selectedGLSummary?.budgetPeriodEndDate || null)})
                </span>
              </span>
            </p>
          </div>
        }
        open={isOpenTransaction}
        onClose={closeTransaction}
      >
        <TransactionTable
          glCodeId={selectedGLSummary?.glCdId || 0}
          glPeriodId={selectedGLSummary?.glPeriodId}
          isOpenTransaction={isOpenTransaction}
        />
        <DialogActionsBar>
          <div className="flex justify-end">
            <Button onClick={closeTransaction} color="info">
              {t('Close')}
            </Button>
          </div>
        </DialogActionsBar>
      </Dialog>
    </div>
  );
};

export default GLSummaryGrid;
